/**
 * Change Flight eligibility: non-Basic Economy, Standard or tier up, or Refundable fares.
 * Basic Economy is excluded; Main Cabin, Comfort+, Refundable, etc. are eligible.
 * Also restricted to major airlines with broad route networks (core 5–15 most booked).
 *
 * Uses getMarketingFareName (same sources as FlightDetailsModal/EditCart):
 * bookingOption.option_title → custom_title → selectedProduct → cabin_class_marketing_name
 * → segment marketing names → cabin_class.
 */
// Explicit .js extension so this module also loads under plain Node ESM —
// the Express server imports it to compute Tommy's trip capabilities.
import { getMarketingFareName, isBasePremiumFareBrand } from './marketingFareName.js';

/** IATA codes for airlines eligible for Change Flight (major carriers with broad route networks) */
export const CHANGE_FLIGHT_ALLOWED_AIRLINES = [
  'AA', 'UA', 'DL', 'WN', 'B6', 'AS',  // US: American, United, Delta, Southwest, JetBlue, Alaska
  'BA', 'LH', 'AF', 'KL', 'VS', 'IB', 'EI',  // Europe: British Airways, Lufthansa, Air France, KLM, Virgin Atlantic, Iberia, Aer Lingus
  'EK', 'QR', 'SQ', 'CX', 'NH', 'JL',  // Middle East/Asia: Emirates, Qatar, Singapore, Cathay Pacific, ANA, Japan Airlines
];

/** Customer-facing names, same order as CHANGE_FLIGHT_ALLOWED_AIRLINES —
 * interpolated into Tommy's knowledge base so the prompt can never drift. */
export const CHANGE_FLIGHT_ALLOWED_AIRLINE_NAMES = [
  'American', 'United', 'Delta', 'Southwest', 'JetBlue', 'Alaska',
  'British Airways', 'Lufthansa', 'Air France', 'KLM', 'Virgin Atlantic', 'Iberia', 'Aer Lingus',
  'Emirates', 'Qatar', 'Singapore', 'Cathay Pacific', 'ANA', 'Japan Airlines',
];

/**
 * A trip is "booked with the airline" once it carries an airline confirmation /
 * record locator (PNR). Defera's internal trip_reference / booking_reference is
 * ALWAYS present, so we specifically inspect the airline confirmation fields.
 *
 * Until that exists, Defera hasn't actually purchased a ticket yet — the
 * customer is effectively still picking their flight. The post-booking change
 * protections (no Basic Economy, Defera Zero must stay refundable) therefore do
 * NOT apply, and the customer may freely switch to any fare brand / cabin on the
 * same route.
 */
export function tripHasAirlineConfirmation(trip) {
  const bookings = Array.isArray(trip) ? trip : trip ? [trip] : [];
  const main = bookings[0];
  if (!main) return false;
  const fd = main.flightData || main.flight_data || {};
  const conf =
    main.confirmation_number ?? main.confirmationNumber ??
    fd.confirmation_number ?? fd.confirmationNumber ?? fd.airline_confirmation ?? '';
  return !!String(conf || '').trim();
}

/** Get airline IATA and name from trip (first segment's marketing carrier) */
export function getTripAirline(trip) {
  const bookings = Array.isArray(trip) ? trip : trip ? [trip] : [];
  const main = bookings[0];
  if (!main) return { iata: null, name: null };
  const fd = main.flightData || main.flight_data || {};
  const seg = fd?.slices?.[0]?.segments?.[0];
  const carrier = seg?.marketing_carrier;
  const iata = (carrier?.iata_code || '').toString().trim().toUpperCase() || null;
  const name = (carrier?.name || main?.marketing_airline || '').toString().trim() || null;
  return { iata, name };
}

/**
 * Is this fare brand the airline's basic-economy product — no changes, no
 * cancellations, no credit?
 *
 * Matching on the literal word "basic" alone is not enough: several carriers
 * brand the same restrictive product under a different name, and those fares
 * slipped through as changeable. Real case — booking WHNYBF, an ISSUED Alaska
 * **Saver** ticket, rendered a self-serve Change Flight button and the desk
 * repeated the offer to the customer. Defera's change search reprices and
 * rebooks, which cannot touch an issued ticket on a fare the airline won't
 * modify at all.
 *
 * Word boundaries matter: a bare /light/ matches inside "Flight", which would
 * misclassify e.g. "Flight Flex" as restrictive.
 *
 * Known brands in this set: Alaska **Saver**, Delta/United/American/Air Canada
 * **Basic Economy**, JetBlue **Blue Basic**, Hawaiian **Main Cabin Basic**,
 * Lufthansa group **Economy Light**, Air France/KLM **Light**, BA **Basic**,
 * and the restricted base tier of a premium cabin: United **Base Business**
 * and **Base Premium Economy** (no changes, non-refundable — TS2MFP). Only
 * those exact labels: a bare "base" would also catch JetBlue "Main Base".
 *
 * Deliberately errs toward restricting. A false positive costs a customer the
 * in-app path and routes them to the concierge (degraded, still served); a
 * false negative walks them into a flow that cannot work — the failure this
 * exists to prevent. The one known trade-off is Emirates "Economy Saver",
 * which IS changeable for a fee; it is caught here and handled by the team.
 */
export function isRestrictiveFareBrand(fareName) {
  return /\b(saver|basic|light)\b/i.test(String(fareName || '')) || isBasePremiumFareBrand(fareName);
}

function getCabinTier(text) {
  const t = (text || '').toLowerCase();
  if (t.includes('first')) return 3;
  if (t.includes('business') || t.includes('delta one')) return 2;
  if (t.includes('premium') && (t.includes('economy') || t.includes('select'))) return 1;
  if (isRestrictiveFareBrand(t)) return -1; // Basic Economy, under any brand name
  return 0; // economy, main cabin, standard, comfort+, etc.
}

function isRefundable(flightData, fareName) {
  const title = (fareName || '').toLowerCase();
  const exts =
    (
      flightData?.bookingOption?.together ||
      flightData?.bookingOption?.departing ||
      flightData?.booking_option?.together ||
      flightData?.booking_option?.departing ||
      {}
    )?.extensions?.map((e) => e?.text || '')
      .join(' ')
      .toLowerCase() || '';
  const combined = `${title} ${exts}`.toLowerCase();
  if (combined.includes('non-refundable') || combined.includes('no refund') || combined.includes('no flex')) return false;
  const refundableTitles = [
    'main select',
    'refundable',
    'flex',
    'fully refundable',
    'economy flex',
    'refundable fare',
    'anytime',
    'business select'
  ];
  if (refundableTitles.some((r) => title.includes(r))) return true;
  return exts.includes('refundable') || exts.includes('free cancellation');
}

/** Statuses meaning the plan is paid off and the ticket issued (mirrors
 * COMPLETED_STATUSES in server/tommy-booking-context.js). */
const COMPLETED_TRIP_STATUSES = ['completed', 'ticketed', 'paid_in_full', 'paid', 'fulfilled'];

/**
 * How tapping Flight Options → Change Flight should route for this trip.
 *
 * - 'airline_direct' — the plan is completed and the ticket issued. Defera's
 *   change-search reprices and rebooks, which can't modify an issued airline
 *   ticket, so the customer makes date/time changes directly with the airline
 *   using their confirmation number.
 * - 'inquiry' — active Slice Pay plan ('installments'). Those plans have no
 *   in-app schedule for the change-search to validate new dates against (it
 *   dead-ends on the default 22-day calendar floor), so the customer messages
 *   support and the team works the change as an inquiry. Completed trips with
 *   no PNR on file also land here — the airline can't help without one.
 * - 'self_service' — everything else: the in-app change-search flow, still
 *   subject to isChangeFlightEligible (fare/airline rules).
 *
 * @param {Object} trip - Array of bookings (or single booking)
 * @returns {'airline_direct' | 'inquiry' | 'self_service'}
 */
export function getChangeFlightRouting(trip) {
  const bookings = Array.isArray(trip) ? trip : trip ? [trip] : [];
  const main = bookings[0];
  if (!main) return 'self_service';
  const status = String(main.trip_status || main.status || '').toLowerCase();
  if (COMPLETED_TRIP_STATUSES.includes(status)) {
    return tripHasAirlineConfirmation(trip) ? 'airline_direct' : 'inquiry';
  }
  const fd = main.flightData || main.flight_data || {};
  // Missing payment method means a legacy Slice Pay booking (same default as
  // computeTripCapabilities) — Zero/Capital bookings always stamp theirs.
  const method = String(fd._payment_method || fd.payment_method || main.payment_method || 'installments');
  if (method === 'installments') return 'inquiry';
  return 'self_service';
}

/**
 * @param {Object} trip - Array of bookings (or single booking) from Dashboard
 * @returns {{ eligible: boolean, reason?: string, airlineIneligible?: boolean }}
 */
export function isChangeFlightEligible(trip) {
  const bookings = Array.isArray(trip) ? trip : trip ? [trip] : [];
  const main = bookings[0];
  if (!main) return { eligible: false, reason: 'No booking found' };

  const { iata: airlineIata, name: airlineName } = getTripAirline(trip);
  const airlineDisplay = airlineName || airlineIata || 'this airline';

  // Change Flight only for major airlines with broad route networks
  if (airlineIata && !CHANGE_FLIGHT_ALLOWED_AIRLINES.includes(airlineIata.toUpperCase())) {
    return {
      eligible: false,
      reason: `Change Flight is not available for ${airlineDisplay} flights. It's only available for major airlines like American, United, Delta, Southwest, JetBlue, and others.`,
      airlineIneligible: true
    };
  }

  const flightData = main.flightData || main.flight_data || {};
  const fareName = getMarketingFareName(main);
  const fareNameLower = (fareName || '').toLowerCase();

  /**
   * Before the airline ticket is purchased (no PNR / confirmation yet) the
   * customer is still effectively choosing their flight, so changes are allowed
   * for ANY fare brand — including Basic Economy. The Basic Economy block only
   * applies once Defera has actually booked the ticket.
   */
  const isBooked = tripHasAirlineConfirmation(trip);

  // Basic Economy is not eligible — but only once booked. Brand-aware, so
  // Alaska "Saver" and friends are caught, not just the literal word "basic".
  if (isBooked && isRestrictiveFareBrand(fareNameLower)) {
    const brand = (fareName || '').trim();
    let reason = 'Basic Economy fares cannot be changed';
    if (isBasePremiumFareBrand(brand)) {
      reason = `${brand} fares cannot be changed — it's the airline's most restrictive fare in that cabin`;
    } else if (brand && !/basic/i.test(brand)) {
      reason = `${brand} fares cannot be changed — it's the airline's basic economy fare`;
    }
    return { eligible: false, reason };
  }

  const cabinTier = getCabinTier(fareName);

  // Refundable fares are eligible
  if (isRefundable(flightData, fareName)) {
    return { eligible: true };
  }

  // Standard or tier up (economy, main cabin, comfort+, etc.) — cabinTier >= 0
  if (cabinTier >= 0) {
    return { eligible: true };
  }

  // No explicit "basic" found and cabinTier is 0 or higher — eligible
  return { eligible: true };
}
